import { personDisplayName } from './apiNormalize';
import type { InvoiceProduct } from './products-table';

/**
 * Pure builder for the invoice/quote submit payload, extracted from
 * `generate-invoice-form.tsx`'s `handleSubmit`. It only assembles an object
 * (money values are passed through as already-computed numbers, never rendered
 * or `formatCurrency`'d), so it's wall-neutral; it also lives under the
 * financial-only-allowlisted `generate-invoice-form/` tree.
 *
 * The branching that's worth pinning in tests: `po_number` is invoice-only,
 * `scheduling` is invoice-only (recurring-creation only — there is no customer
 * "send out" anymore), `day_of_week` only rides weekly recurrence and
 * `day_of_month` only the monthly/quarterly/yearly ones, and product rows with
 * a blank description are dropped. The server re-derives the money fields
 * authoritatively on save — we send the consistent values so optimistic UI
 * matches, but they are not authoritative here.
 */
export interface InvoicePayloadContact {
  id: number | string;
  full_name?: string | null;
  name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  company_name?: string | null;
  email?: string | null;
}

export interface InvoicePayloadFormData {
  document_type: 'invoice' | 'quote';
  invoice_number: string;
  subject: string;
  issue_date: string;
  due_date: string;
  service_from: string;
  service_to: string;
  po_number: string;
  sales_person: string;
  terms: string;
  bill_to_company: string;
  bill_to_address: string;
  bill_to_city: string;
  bill_to_state: string;
  bill_to_zip: string;
  bill_to_country: string;
  ship_to_company: string;
  ship_to_address: string;
  ship_to_city: string;
  ship_to_state: string;
  ship_to_zip: string;
  ship_to_country: string;
  bill_to_contact_id: number | null;
  ship_to_contact_id: number | null;
  products: InvoiceProduct[];
  market_discount: number;
  reseller_discount: number;
  additional_discount_amount: number;
  additional_discount_percentage: number;
  tax_percentage: number;
}

export interface InvoicePayloadTotals {
  subTotal: number;
  taxAmount: number;
  totalAmount: number;
  balanceDue: number;
}

export interface InvoicePayloadScheduling {
  enableRecurring: boolean;
  recurringFrequency: 'weekly' | 'monthly' | 'quarterly' | 'yearly';
  recurringInterval: number;
  recurringDayOfWeek: number;
  recurringDayOfMonth: number;
  recurringStartDate: string;
  // Days before each billing period ends at which the invoice is generated.
  // Default 1 = the period's last day (invoices bill in arrears).
  recurringGenerateDaysBeforePeriodEnd: number;
}

export function buildInvoicePayload(input: {
  /** The customer being billed, or NULL when the document is addressed to a
   * RESELLER itself (2026-09-13). XOP's business with a reseller has no
   * customer and no deal behind it — crm-api has always allowed such a row
   * (it is the shape the portal quote mirror writes), and this is the command
   * center raising one deliberately. */
  contact: InvoicePayloadContact | null;
  dealId: number | null;
  formData: InvoicePayloadFormData;
  totals: InvoicePayloadTotals;
  scheduling: InvoicePayloadScheduling;
  /** The RECIPIENT reseller (2026-09-12): the customer's reseller or one of
   * its dealers, when the Bill To dropdown picked one; null bills the customer.
   * Stored as `bill_to_reseller_id` so the document lists on the reseller's
   * profile — the `bill_to_*` strings alone were only a snapshot. */
  billToResellerId?: number | null;
}) {
  const { contact, dealId, formData, totals, scheduling } = input;

  return {
    customer_id: contact?.id ?? null,
    customer_deal_id: dealId,
    bill_to_reseller_id: input.billToResellerId ?? null,
    document_type: formData.document_type,
    invoice_number: formData.invoice_number.trim(),
    subject: formData.subject.trim(),
    total_amount: totals.totalAmount,
    issue_date: formData.issue_date,
    due_date: formData.due_date,
    // Service period (the window the invoice bills for) is invoice-only —
    // quotes have no service window, so blank it there like po_number.
    // Sent on both create AND update via this single builder; crm-api's
    // date columns coerce '' → null, so clearing a date on edit persists.
    service_from: formData.document_type === 'invoice' ? formData.service_from : '',
    service_to: formData.document_type === 'invoice' ? formData.service_to : '',
    // P.O. number is for invoices only, not quotes.
    po_number: formData.document_type === 'invoice' ? formData.po_number : '',
    sales_person: formData.sales_person,
    terms: formData.terms,

    // Customer information (frozen at time of invoice creation).
    // On a reseller-billed document these are the reseller's own details,
    // already in the bill_to_* fields; the customer_* columns are the
    // document's "who is this for" snapshot and are left to the bill-to
    // company rather than invented.
    customer_name: contact ? personDisplayName(contact) : '',
    customer_company_name: contact?.company_name || formData.bill_to_company || '',
    customer_email: contact?.email || '',

    // Addresses.
    bill_to_company: formData.bill_to_company,
    bill_to_address: formData.bill_to_address,
    bill_to_city: formData.bill_to_city,
    bill_to_state: formData.bill_to_state,
    bill_to_zip: formData.bill_to_zip,
    bill_to_country: formData.bill_to_country,

    ship_to_company: formData.ship_to_company,
    ship_to_address: formData.ship_to_address,
    ship_to_city: formData.ship_to_city,
    ship_to_state: formData.ship_to_state,
    ship_to_zip: formData.ship_to_zip,
    ship_to_country: formData.ship_to_country,

    // Recipient contact person per section — crm-api snapshots the email/name
    // from the id and rejects a contact from another company. Quotes send the
    // single "To" recipient on the bill_to side.
    bill_to_contact_id: formData.bill_to_contact_id,
    ship_to_contact_id: formData.ship_to_contact_id,

    // Products — drop rows whose description is blank.
    products: formData.products.filter((p) => p.description.trim()),

    // Totals: discount/tax INPUTS are user-entered; the money figures are the
    // single derived `totals`. The server re-derives them authoritatively on
    // save (invoice_params rejects client money fields) — we send consistent
    // values so any optimistic UI matches.
    sub_total: totals.subTotal,
    market_discount: formData.market_discount,
    reseller_discount: formData.reseller_discount,
    additional_discount_amount: formData.additional_discount_amount,
    additional_discount_percentage: formData.additional_discount_percentage,
    tax_percentage: formData.tax_percentage,
    tax_amount: totals.taxAmount,
    balance_due: totals.balanceDue,

    // Scheduling options (only for invoices). Recurring schedules CREATE
    // invoices on a cadence — they never send to the customer (the generated
    // invoice is emailed to finance for review by the backend), so there is no
    // auto-send / recipient config here anymore.
    scheduling:
      formData.document_type === 'invoice'
        ? {
            recurring: scheduling.enableRecurring
              ? {
                  frequency: scheduling.recurringFrequency,
                  interval: scheduling.recurringInterval,
                  day_of_week:
                    scheduling.recurringFrequency === 'weekly'
                      ? scheduling.recurringDayOfWeek
                      : undefined,
                  day_of_month: ['monthly', 'quarterly', 'yearly'].includes(
                    scheduling.recurringFrequency,
                  )
                    ? scheduling.recurringDayOfMonth
                    : undefined,
                  start_date: scheduling.recurringStartDate,
                  generate_days_before_period_end:
                    scheduling.recurringGenerateDaysBeforePeriodEnd,
                }
              : null,
          }
        : undefined,
  };
}
